import { useState, useCallback } from 'react';
import { useMutation } from '@apollo/client';
import { gql } from 'graphql-tag';
import { CustomFieldValueFragmentDoc } from '~/types';
import specificResourceFragment from '~/modules/resource-management/enhancers/specificResourceFragment';
import { useHasFeatureFlag } from '~/modules/common/hooks';

export const UPDATE_USER_CUSTOM_FIELDS_MUTATION = gql`
  mutation UpdateUserCustomFields($input: UpdateUserCustomFieldInput!) {
    updateUserCustomFields2(input: $input) {
      customFieldValues {
        ...CustomFieldValue
      }
    }
  }
  ${CustomFieldValueFragmentDoc}
`;

export const updateCache = ({ userUri, isPsaPraaUserProfileNudgesEnabled }) => (
  proxy,
  mutationResponse
) => {
  const {
    data: {
      updateUserCustomFields2: { customFieldValues }
    }
  } = mutationResponse;

  const cachedFragmentId = `ResourceDetails:${userUri}`;

  const cachedUser = proxy.readFragment({
    id: cachedFragmentId,
    fragment: specificResourceFragment,
    fragmentName: 'SpecificResourceFragment',
    variables: { isPsaPraaUserProfileNudgesEnabled }
  });

  proxy.writeFragment({
    id: cachedFragmentId,
    fragmentName: 'SpecificResourceFragment',
    fragment: specificResourceFragment,
    variables: { isPsaPraaUserProfileNudgesEnabled },
    data: {
      ...cachedUser,
      customFieldValues
    }
  });
};

export const useUpdateUserCustomFieldsMutation = () =>
  useMutation(UPDATE_USER_CUSTOM_FIELDS_MUTATION);

const useUpdateUserCustomFields = ({ mapValuesOnSubmit, onClose }) => {
  const [error, setError] = useState();
  const isPsaPraaUserProfileNudgesEnabled = useHasFeatureFlag({
    featureFlag: 'isPsaPraaUserProfileNudgesEnabled'
  });

  const clearError = useCallback(() => setError(null), [setError]);

  const [updateUserCustomFields] = useUpdateUserCustomFieldsMutation();

  const onSubmit = useCallback(
    async (values, { setSubmitting }) => {
      const payload = mapValuesOnSubmit ? mapValuesOnSubmit(values) : values;

      try {
        await updateUserCustomFields({
          variables: {
            input: payload
          },
          update: updateCache({
            userUri: values.id,
            isPsaPraaUserProfileNudgesEnabled
          })
        });
      } catch (err) {
        setError(err);
      } finally {
        setSubmitting(false);
        onClose();
      }
    },
    [
      mapValuesOnSubmit,
      updateUserCustomFields,
      isPsaPraaUserProfileNudgesEnabled,
      onClose
    ]
  );

  return { onSubmit, error, clearError };
};

export default useUpdateUserCustomFields;
